import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import WebhookIcon from '@mui/icons-material/Webhook';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { DragModal } from 'components/DragModal/DragModal';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import SkeletonBox from 'components/generic/SkeletonBox';
import {
  BroadcastTargetItem,
  GetBroadcastTargetResult,
} from 'providers/contexts/extensions/Broadcast';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useIntersection } from '@mantine/hooks';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { bloxValues } from 'themes/constants';

// ---------------------------------------------------------
// Types
// ---------------------------------------------------------

type SelectedTarget = {
  id: string;
  driver: string;
  name?: string;
};

type DeliveryTargetPickerModalProps = {
  open: boolean;
  onClose: () => void;
  selectedTargets: SelectedTarget[];
  onAccept: (targets: SelectedTarget[]) => void;
};

// ---------------------------------------------------------
// Constants
// ---------------------------------------------------------

const PAGE_SIZE = 25;

// ---------------------------------------------------------
// Helpers
// ---------------------------------------------------------

const getTargetIcon = (driver: string) => {
  switch (driver?.toLowerCase()) {
    case 'appmessages':
    case 'mobileapp':
      return PhoneAndroidIcon;
    case 'facebook':
      return FacebookIcon;
    case 'webhook':
      return WebhookIcon;
    case 'pushnotification':
      return NotificationsActiveIcon;
    default:
      return NotificationsActiveIcon;
  }
};

const getDriverLabel = (driver: string, t: (key: string) => string) => {
  switch (driver?.toLowerCase()) {
    case 'appmessages':
      return t('blox.notifier.channel.targets.mobile_app');
    case 'facebook':
      return t('blox.notifier.channel.targets.facebook');
    case 'webhook':
      return t('blox.notifier.channel.targets.webhook');
    case 'pushnotification':
      return t('blox.notifier.channel.targets.push_notification');
    default:
      return driver;
  }
};

// ---------------------------------------------------------
// Delivery Target Picker Modal
// ---------------------------------------------------------

export const DeliveryTargetPickerModal = memo(
  ({ open, onClose, selectedTargets, onAccept }: DeliveryTargetPickerModalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();

    // Local selection state (committed on accept)
    const [localSelection, setLocalSelection] = useState<SelectedTarget[]>([]);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    // Intersection observer for infinite scroll
    const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });

    // Translation strings
    const strSelectTargets = t('blox.notifier.channel.select_delivery_targets');
    const strAccept = t('blox.actions.accept');
    const strCancel = t('blox.actions.cancel');
    const strLoading = t('blox.general.loading');
    const strNoTargets = t('blox.notifier.no_channels_found');
    const strEndOfResults = t('blox.general.end_of_results');
    const strSelected = t('blox.general.selected');

    // Reset local selection when modal opens
    useEffect(() => {
      if (open) {
        setLocalSelection([...selectedTargets]);
        setCollapsedGroups(new Set());
      }
    }, [open, selectedTargets]);

    // Infinite query for targets
    const {
      data,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      isLoading,
    } = useInfiniteQuery<
      GetBroadcastTargetResult,
      Error,
      InfiniteData<GetBroadcastTargetResult>,
      string[],
      number
    >({
      queryKey: ['broadcast/target', 'picker'],
      queryFn: async ({ pageParam }) => {
        const result = await dataProvider.getBroadcastTarget({
          query: {
            page: pageParam,
            limit: PAGE_SIZE,
            sort: 'name',
            dir: 'asc',
          },
        });
        return result;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const next = lastPage?.data?.next;
        return next ? Number(next) : undefined;
      },
      enabled: open,
      staleTime: 5 * 60 * 1000,
    });

    // Flatten all pages into a single list
    const allTargets = useMemo(() => {
      return data?.pages?.flatMap((page) => page?.data?.items ?? []) ?? [];
    }, [data]);

    // Group targets by driver type
    const groupedTargets = useMemo(() => {
      return allTargets.reduce(
        (acc, target) => {
          const driver = target.driver || 'other';
          if (!acc[driver]) {
            acc[driver] = [];
          }
          acc[driver].push(target);
          return acc;
        },
        {} as Record<string, BroadcastTargetItem[]>,
      );
    }, [allTargets]);

    const driverKeys = useMemo(() => Object.keys(groupedTargets), [groupedTargets]);

    // Toggle a target in local selection
    const handleToggle = useCallback((target: BroadcastTargetItem) => {
      setLocalSelection((prev) => {
        const isSelected = prev.some((t) => t.id === target.id);
        if (isSelected) {
          return prev.filter((t) => t.id !== target.id);
        }
        return [...prev, { id: target.id, driver: target.driver, name: target.name }];
      });
    }, []);

    // Toggle all targets in a driver group
    const handleToggleGroup = useCallback(
      (driver: string, targets: BroadcastTargetItem[]) => {
        setLocalSelection((prev) => {
          const allSelected = targets.every((t) => prev.some((s) => s.id === t.id));
          if (allSelected) {
            const groupIds = new Set(targets.map((t) => t.id));
            return prev.filter((s) => !groupIds.has(s.id));
          }
          const existing = new Set(prev.map((s) => s.id));
          const newItems = targets
            .filter((t) => !existing.has(t.id))
            .map((t) => ({ id: t.id, driver: t.driver, name: t.name }));
          return [...prev, ...newItems];
        });
      },
      [],
    );

    const handleToggleCollapse = useCallback((driver: string) => {
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(driver)) {
          next.delete(driver);
        } else {
          next.add(driver);
        }
        return next;
      });
    }, []);

    const handleAccept = useCallback(() => {
      onAccept(localSelection);
      onClose();
    }, [localSelection, onAccept, onClose]);

    const selectedCount = localSelection.length;

    return (
      <DragModal
        open={open}
        onClose={onClose}
        isDraggable={true}
        isResizable={true}
        initialWidth={640}
        initialHeight={560}
        minWidth={440}
        minHeight={360}
        preventBackdropClickClose={true}
        preventEscapeKeyClose={true}
        header={
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography sx={{ fontWeight: bloxValues.fontWeightMedium }}>
              {strSelectTargets}
            </Typography>
            {selectedCount > 0 && (
              <Chip
                label={`${selectedCount} ${strSelected.toLowerCase()}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: bloxValues.fontWeightMedium }}
              />
            )}
          </Stack>
        }
        body={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {/* Scrollable Target List */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                scrollbarGutter: 'stable',
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  <SkeletonBox
                    text={strLoading}
                    height={200}
                  />
                </Box>
              ) : allTargets.length === 0 ? (
                <Box
                  sx={{
                    m: 2,
                    p: 3,
                    textAlign: 'center',
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {strNoTargets}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ py: 0.5 }}>
                  {driverKeys.map((driver) => {
                    const targets = groupedTargets[driver];
                    const Icon = getTargetIcon(driver);
                    const isCollapsed = collapsedGroups.has(driver);
                    const allInGroupSelected = targets.every((t) =>
                      localSelection.some((s) => s.id === t.id),
                    );
                    const someInGroupSelected =
                      !allInGroupSelected &&
                      targets.some((t) => localSelection.some((s) => s.id === t.id));
                    const selectedInGroup = targets.filter((t) =>
                      localSelection.some((s) => s.id === t.id),
                    ).length;

                    return (
                      <Box key={driver}>
                        {/* Driver Group Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.action.hover,
                            '&:hover': {
                              bgcolor: theme.palette.action.selected,
                            },
                          }}
                          onClick={() => handleToggleCollapse(driver)}
                        >
                          <Checkbox
                            checked={allInGroupSelected}
                            indeterminate={someInGroupSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleGroup(driver, targets);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                          <Icon
                            sx={{ fontSize: bloxValues.iconSizeSmall }}
                            color="action"
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: bloxValues.fontWeightMedium,
                              flex: 1,
                            }}
                          >
                            {getDriverLabel(driver, t)}
                          </Typography>
                          {selectedInGroup > 0 && (
                            <Chip
                              label={selectedInGroup}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: bloxValues.tinyFontSizeRem,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              mr: 0.5,
                            }}
                          >
                            {targets.length}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleCollapse(driver);
                            }}
                            sx={{
                              p: 0.25,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {isCollapsed ? (
                              <ExpandMoreIcon fontSize="small" />
                            ) : (
                              <ExpandLessIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>

                        {/* Collapsible Targets in Group */}
                        <Collapse in={!isCollapsed}>
                          <FormGroup sx={{ pl: 2, py: 0.25 }}>
                            {targets.map((target) => {
                              const isChecked = localSelection.some(
                                (s) => s.id === target.id,
                              );
                              return (
                                <FormControlLabel
                                  key={target.id}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={() => handleToggle(target)}
                                      size="small"
                                      sx={{ p: 0.5 }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: bloxValues.smallFontSizeRem,
                                      }}
                                    >
                                      {target.name || target.id}
                                    </Typography>
                                  }
                                  sx={{
                                    mx: 0,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    '&:hover': {
                                      bgcolor: theme.palette.action.hover,
                                    },
                                    ...(isChecked && {
                                      bgcolor: theme.palette.action.selected,
                                    }),
                                  }}
                                />
                              );
                            })}
                          </FormGroup>
                        </Collapse>
                      </Box>
                    );
                  })}

                  {/* Infinite Scroll Loader */}
                  <IntersectionLoader
                    hasNextPage={hasNextPage}
                    isFetching={isFetchingNextPage || isFetching}
                    fetchNextPage={fetchNextPage}
                    ixRef={ixRef}
                    entry={entry}
                    endOfResultsMessage={
                      allTargets.length > 0 ? strEndOfResults : undefined
                    }
                    compact
                    hideOnPagesEnd={allTargets.length < PAGE_SIZE}
                  />
                </Box>
              )}
            </Box>
          </Box>
        }
        footer={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              width: '100%',
            }}
          >
            <Button onClick={onClose}>{strCancel}</Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleAccept}
            >
              {strAccept}
              {selectedCount > 0 && ` (${selectedCount})`}
            </Button>
          </Box>
        }
      />
    );
  },
);
DeliveryTargetPickerModal.displayName = 'DeliveryTargetPickerModal';
